<template>
  <el-card
    id="categoryEditorCard"
    class="mainCard clear-card defaultCursor"
  >
    <div class="title">
      <div class="left">
        {{ $t('coach.category.editor.title') }}
      </div>
      <div class="right">
        <el-button
          :type="edit ? 'plain' : 'primary'"
          round
          :loading="loading"
          @click="clickEdit"
        >
          <span v-if="!edit">
            {{ $t('common.edit') }} <i class="el-icon-edit"></i>
          </span>
          <span v-else>
            {{ $t('common.save') }}
          </span>
        </el-button>
      </div>
    </div>

    <div class="body">
      <p>
        <em>
          <i18n path="coach.category.editor.desc">
            <template #sow>
              <a
                class="coloredAnchor"
                href="https://app.box.com/s/e51ru0deh27r4xiixnyy96dwxqi3h94h/"
                target="_blank"
              >
                {{ $t('coach.category.editor.sow') }}
              </a>
            </template>
            <template #feeTerm>
              <a
                class="coloredAnchor"
                href="https://info.gcgroup.io/platform/payouts/"
                target="_blank"
              >
                {{ $t('coach.category.editor.feeTerm') }}
              </a>
            </template>
          </i18n>
        </em>
      </p>

      <p style="padding: 1em 0;">
        <b>
          {{ $t('coach.category.recommendedAvail') }}: 10
          <!--{{ user.profile.max_engagement_ct }}-->
          <!--<el-tooltip
            effect="dark"
            placement="top"
            :content="`Leaders you invite onto the platform are not counted toward this maximum.
            If you need more engagements, please contact us`"
          >
            <i class="el-icon-info"></i>
          </el-tooltip>-->
        </b>
      </p>

      <el-form
        ref="categoryForm"
        :model="categoryForm"
      >
        <el-row
          v-for="(key, index) in Object.keys(categoryForm)"
          :key="`category${key}`"
          :gutter="20"
          style="display: flex; align-items: stretch; flex-wrap: wrap;"
        >
          <el-col :span="responsiveCol.one">
            <div class="customLabel">
              <div class="number">
                {{ index + 1 }}.
              </div>
              <div>
                {{ $t(`coachingTier.${key}`) }}
                <span class="caption">
                  {{ $t(`coachingTier.${key}_caption`) }}
                </span>
              </div>
            </div>
          </el-col>
          <el-col
            :span="responsiveCol.two"
            class="rightCol"
            :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
          >
            <div v-if="!edit">
              <el-input
                v-if="typeof categoryForm[key] === 'number' && categoryForm[key] >= 0"
                :value="categoryForm[key]"
                :loading="loading"
                class="staticValue"
                disabled
              />
              <i
                v-else
                class="emptyPlaceholder"
              >
                -
              </i>
            </div>
            <el-form-item
              v-else
              :key="`categoryItem${key}`"
              :prop="`${key}`"
              :rules="[
                {
                  required: true,
                  message: `${$t('common.rule.category.requiredAvail')}`,
                  trigger: ['blur', 'change'],
                },
                {
                  type: 'number',
                  message: `${$t('common.rule.category.mustNumber')}`,
                  trigger: ['blur', 'change'],
                }
              ]"
            >
              <el-input
                v-model.number="categoryForm[key]"
                :value="categoryForm[key]"
                type="number"
                :loading="loading"
                :disabled="loading"
                class="staticValue editor"
                size="medium"
                @change="setChangeFlag"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <el-divider />

      <el-row
        :gutter="20"
        style="display: flex; align-items: stretch; flex-wrap: wrap;"
      >
        <el-col :span="responsiveCol.one">
          <span></span>
        </el-col>
        <el-col
          :span="responsiveCol.two"
          class="totalCol"
          :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
        >
          <div class="totalContent">
            <div class="totalLabel">
              {{ $t('coach.category.total') }}:
            </div>
            <div class="totalValue">
              {{ engagementSummary }}
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </el-card>
</template>

<script>
import api from '@/api';
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'CategoryEditor',

  props: {
    categories: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      changeFlag: false,
      edit: false,
      categoryForm: {
        tier_1: 0,
        tier_2: 0,
        tier_3: 0,
        tier_4: 0,
        tier_5: 0,
        tier_6: 0,
        tier_7: 0,
        tier_8: 0,
      },
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    responsiveCol() {
      const col = {
        one: 14,
        two: 10,
      };
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.one = 24;
        col.two = 24;
      }
      return col;
    },

    engagementSummary() {
      let summary = 0;
      Object.keys(this.categoryForm).forEach((key) => {
        if (this.categoryForm[key] && typeof this.categoryForm[key] === 'number') {
          summary += this.categoryForm[key];
        } else {
          summary += 0;
        }
      });
      return summary;
    },
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.initForm();
      },
    },
  },

  mounted() {
    this.initForm();
  },

  methods: {
    ...mapMutations('user', [
      'loginUser',
    ]),

    setChangeFlag() {
      this.changeFlag = true;
    },

    initForm() {
      this.loading = true;
      if (this.user && this.user.profile && this.user.profile.display_tier_list) {
        this.user.profile.display_tier_list.forEach((el) => {
          this.categoryForm[`tier_${el.tier}`] = el.max_engagement_ct;
        });
      }
      this.loading = false;
    },

    async clickEdit() {
      if (this.edit) {
        await this.saveSettings();
      } else {
        this.edit = !this.edit;
      }
    },

    async saveSettings() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (valid) {
          if (this.engagementSummary <= 200) {
            try {
              if (this.changeFlag) {
                this.loading = true;
                const sendData = { profile: {} };
                sendData.profile.display_tier_list = Object.keys(this.categoryForm)
                  .filter((key) => this.categoryForm[key])
                  .map((key) => ({
                    tier: key.split('_')[1],
                    max_engagement_ct: this.categoryForm[key],
                  }));
                const response = await api.updateUserProfile(sendData);
                await this.loginUser(response.data);
                this.$message.success('Availability settings successfully saved.');
                this.changeFlag = false;
              }
              this.edit = false;
            } catch (err) {
              console.log(err);
              if (err.msg) {
                this.$message.error(err.msg);
              }
            } finally {
              this.loading = false;
            }
          } else {
            this.$message.error('Please make sure your total availability does not exceed 200 engagements.');
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  #categoryEditorCard {
    .title {
      margin-bottom: 12px;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: default;

      .left {
        font-size: 18px;
      }
    }

    .body {
      padding: 20px 0 0;
      line-height: 1.4rem;

      .coloredAnchor {
        color: var(--themeFontColor);
      }

      .el-row {
        display: flex;
        align-items: stretch;
        padding: 8px 0;

        .el-col {
          display: flex;
          align-items: center;

          &.rightCol {
            &.deskMode {
              justify-content: center;
            }
          }

          &.totalCol {
            justify-content: center;

            &.mobileMode {
              justify-content: flex-start;

              .totalContent {
                justify-content: start;
              }
            }

            .totalContent {
              width: 100%;
              display: flex;
              justify-content: center;
              font-weight: bold;

              .totalLabel {
                width: 3px;
              }

              .totalValue {
                width: 230px;
                text-align: center;
              }
            }
          }
        }
      }

      .customLabel {
        display: flex;
        align-items: flex-start;
        min-height: 40px;

        .number {
          padding-right: 4px;
        }

        .caption {
          font-size: 15px;
          font-style: italic;
          opacity: 0.7;
        }
      }

      .staticValue {
        display: inline-block;
        width: calc(100%);
        max-width: 100px;
        border: 1px solid var(--themeFontColor);
        height: 37.5px;
        margin: 0;
        font-size: 1rem;
        border-radius: 8px;
        padding: 0;
        text-align: center;
        overflow: hidden;

        &.editor {
          background: white;
          color: black !important;
        }
      }
    }
  }
</style>

<style lang="scss">
  #categoryEditorCard {
    .el-form-item {
      margin: 0;

      .el-form-item__label {
        font-size: 14px;
      }

      .el-form-item__content {
        height: 40px;
        font-size: 16px;
      }

      .el-form-item__error {
        white-space: nowrap;
        overflow: visible;
      }
    }

    .el-input {
      input {
        padding: 0;
        border-bottom-width: 0;
        cursor: initial;
        text-align: center;
      }

      &:not(.is-disabled) {
        .el-input__inner {
          color: black;
        }
      }
    }
  }
</style>
